import { useEffect, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { resetForSite } from '@store/slices/monitor.slice';
import { writeLastSiteId } from '@utils/lastSite';
import {
  selectProject,
  selectSnapshotStatus,
  selectSelectedFloorId,
} from '@store/selectors/monitor.selectors';
import { selectWorkingSite } from '@store/selectors/site.selectors';
import {
  useFilterUrlSync,
  useFloorUrlSync,
  useMonitorSnapshot,
  useMonitorStream,
} from './hooks';
import { FloorViewCard } from './LeftRail/FloorViewCard';
import { LiveSummaryCard } from './LeftRail/LiveSummaryCard';
import { WorkforceTab } from './LeftRail/WorkforceTab';
import { SafetyAlertsCard } from './LeftRail/SafetyAlertsCard';
import { LeftRailPanel } from './LeftRail/LeftRailPanel';
import { BuildingCardControls } from './MainView/BuildingCardControls';
import { MainView } from './MainView/MainView';
import { FloorDrawer } from './MainView/FloorDrawer';
import { EdgeHandle } from '@components/ui/EdgeHandle/EdgeHandle';
import { FullscreenMain } from './fullscreen/FullscreenMain';
import { MonitorFullscreenContext } from './fullscreen/MonitorFullscreenContext';
import { useMonitorFullscreen } from './fullscreen/useMonitorFullscreen';
import { LayerDataProvider } from './layers/LayerDataProvider';
import { MonitorSkeleton } from './MonitorSkeleton';
import { ReplayViewProvider } from './replay/ReplayViewContext';
import { useReplayController } from './replay/useReplayController';
import { ChevronLeftIcon, UsersIcon } from '@utils/icons';
import fullscreenStyles from './fullscreen/fullscreen.module.css';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import { PageBadge, PageSubtitle } from '@components/ui/PageFrame/PageFrame';
import styles from './styles.module.css';

export function MonitorPage() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useAppDispatch();
  const snapshotStatus = useAppSelector(selectSnapshotStatus);
  const project = useAppSelector(selectProject);
  const workingSite = useAppSelector(selectWorkingSite);
  const drawerFloorId = useAppSelector(selectSelectedFloorId);

  // Memoized so its element reference stays stable. `MonitorPage` does not
  // subscribe to `activeTab` — switching tabs happens entirely inside
  // `LeftRailPanel`, and this subtree is skipped by React's reconciler.
  const cardStack = useMemo(
    () => (
      <>
        <FloorViewCard />
        <LiveSummaryCard />
        <WorkforceTab />
        <SafetyAlertsCard />
      </>
    ),
    [],
  );

  useEffect(() => {
    if (siteId) {
      dispatch(resetForSite(siteId));
      writeLastSiteId(siteId);
    }
  }, [siteId, dispatch]);

  useMonitorSnapshot(siteId ?? null);
  useMonitorStream(siteId ?? null);
  // Mount AFTER resetForSite so the per-site reset doesn't wipe the
  // selection we just seeded from `?floor=` / `?prof=` / `?mgr=` / `?eq=`.
  useFloorUrlSync();
  useFilterUrlSync();

  // The page header renders whether the snapshot is loading, failed or loaded
  // (once loaded, a replay's subtitle names the site instead). `workingSite` is hydrated synchronously from
  // localStorage at app bootstrap (via `siteService.getChosenSite()`), so the
  // project name in the subtitle is already correct on first paint; falling
  // back to an em-dash covers a direct deep-link to a site we don't know yet.
  const projectName = project?.name ?? workingSite?.name ?? '—';

  const liveSubtitle = t('monitor.header.subtitle');
  if (snapshotStatus === 'error') {
    return (
      <MonitorShell subtitle={liveSubtitle}>
        <p style={{ padding: '2rem' }}>{t('monitor.snapshotFailed')}</p>
      </MonitorShell>
    );
  }
  if (!project || !siteId) {
    return (
      <MonitorShell subtitle={liveSubtitle}>
        <MonitorSkeleton />
      </MonitorShell>
    );
  }
  // The replay bar names the site as the breadcrumb does, not the upstream project.
  const siteName = workingSite?.siteId === siteId ? workingSite.name : projectName;
  return (
    <LoadedMonitor
      siteId={siteId}
      siteName={siteName}
      tz={project.tz}
      liveSubtitle={liveSubtitle}
      cardStack={cardStack}
      drawerFloorId={drawerFloorId}
    />
  );
}

interface IMonitorShellProps {
  subtitle: string;
  /** The bar is open: the page is in Replay mode, however close to live its playhead. */
  replayMode?: boolean;
  /** Fullscreen: the page covers the app, its header a slim bar holding these controls. */
  fullscreenControls?: ReactNode;
  children: ReactNode;
}

function MonitorShell({ subtitle, replayMode = false, fullscreenControls, children }: IMonitorShellProps) {
  const { t } = useTranslation();
  const title = t('nav.item.monitor.title');
  const badge = (
    <span className={replayMode ? styles.modeReplay : styles.modeLive}>
      {!replayMode && <i aria-hidden className={styles.modeDot} />}
      {t(replayMode ? 'monitor.replay.tag' : 'monitor.replay.live')}
    </span>
  );
  const body = <div className={styles.body}>{children}</div>;
  if (!fullscreenControls) {
    return (
      <>
        <PageBadge>{badge}</PageBadge>
        <PageSubtitle>{subtitle}</PageSubtitle>
        {body}
      </>
    );
  }
  // The app's content pane is its own stacking context, under the sidebar and top bar; only <body> covers them.
  return createPortal(
    <div className={styles.fullscreen}>
      <PageHeader
        className={styles.header}
        title={title}
        subtitle={subtitle}
        badge={badge}
        actions={<div className={styles.fullscreenControls}>{fullscreenControls}</div>}
      />
      {body}
    </div>,
    document.body,
  );
}

interface ILoadedMonitorProps {
  siteId: string;
  /** The site's name, for the replay bar and the replay subtitle (the live one names the project). */
  siteName: string;
  tz: string;
  liveSubtitle: string;
  cardStack: ReactNode;
  drawerFloorId: string | null;
}

/** The loaded Monitor: its replay drives the building, the Live Summary and the subtitle. */
function LoadedMonitor({ siteId, siteName, tz, liveSubtitle, cardStack, drawerFloorId }: ILoadedMonitorProps) {
  const { t } = useTranslation();
  const replay = useReplayController(siteId, siteName, tz);
  const { fullscreen, toggle, toggleRef, railCollapsed, toggleRail } = useMonitorFullscreen({
    replayOpen: replay.open,
    drawerOpen: drawerFloorId !== null,
  });
  // Stable, so a replay tick here does not re-render the memoized main views.
  const fullscreenContext = useMemo(() => ({ fullscreen, toggle, toggleRef }), [fullscreen, toggle, toggleRef]);
  const subtitle = replay.stamp ? t('monitor.replay.subtitle', { site: siteName, ...replay.stamp }) : liveSubtitle;
  const hideRail = fullscreen && railCollapsed;
  return (
    <MonitorFullscreenContext value={fullscreenContext}>
      <MonitorShell
        subtitle={subtitle}
        replayMode={replay.open}
        fullscreenControls={fullscreen && <BuildingCardControls replay={replay} />}
      >
        <ReplayViewProvider value={replay.view}>
          <LayerDataProvider siteId={siteId}>
            <aside className={styles.leftRail} data-collapsed={hideRail} inert={hideRail}>
              <LeftRailPanel cardStack={cardStack} />
            </aside>
            {fullscreen && (
              <EdgeHandle
                className={fullscreenStyles.railHandle}
                aria-expanded={!railCollapsed}
                aria-label={t(railCollapsed ? 'monitor.fullscreen.expandRail' : 'monitor.fullscreen.collapseRail')}
                onClick={toggleRail}
              >
                {railCollapsed ? <UsersIcon size={16} /> : <ChevronLeftIcon size={16} />}
              </EdgeHandle>
            )}
            <section className={styles.mainView}>
              {fullscreen ? <FullscreenMain replay={replay} /> : <MainView replay={replay} />}
            </section>
            {drawerFloorId && <FloorDrawer floorId={drawerFloorId} />}
          </LayerDataProvider>
        </ReplayViewProvider>
      </MonitorShell>
    </MonitorFullscreenContext>
  );
}
